import React, { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import HomeDashboardLayout from "../HomeDashboardLayout";
import "../../dashboard/Dashboard.css";
import "../HomeDashboard.css";
import "../questionBank/QbankDetail.css";
import HeaderProfileSection from "../HeaderProfileSection";
import ReportModal from '../../components/ReportModal';
import StickyNotesPopup from '../../components/StickyNotesPopup';
import ViewModal from '../../components/ViewModal';
import api from "../../services/api";
import { useRapidFireQuestions } from "../../hooks/useRapidFireQuestions";
import { RAPID_FIRE_ROUTES } from "./rapidFireRoutes";
import {
  buildRapidFireFlowState,
  getStoredRapidFireFlowState,
  persistRapidFireFlowState,
} from "./rapidFireFlowState";

function getLocalizedText(item = {}, baseField) {
  return (
    item?.[`${baseField}_en`] ||
    item?.[baseField] ||
    item?.[`${baseField}_hi`] ||
    ""
  );
}

function normalizeOption(option = {}, index = 0) {
  return {
    id: Number(option?.option_id || option?.id || index + 1),
    label: getLocalizedText(option, "option_name") || option?.title || `Option ${index + 1}`,
    isCorrect: String(option?.is_option_correct || "").toUpperCase() === "Y",
    isSelected: Number(option?.user_selected_option || 0) === 1,
    tone: "neutral",
  };
}

const toolbarActions = [
  { icon: "bi-arrow-clockwise", label: "Reset question" },
  { icon: "bi-type", label: "Text settings" },
  { icon: "bi-info-circle", label: "Question info" },
];

const RapidFireDetail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const flowState = useMemo(
    () => buildRapidFireFlowState(location.state, getStoredRapidFireFlowState()),
    [location.state]
  );
  const batchData = flowState?.batchData || flowState?.selectedBatch || {};
  const batchId = Number(batchData?.batch_id || batchData?.batchId || batchData?.id || 0);

  const selectedStateId = Number(flowState?.selectedState?.state_id || flowState?.state_id || 13);
  const selectedCategoryId = Number(
    flowState?.selectedCategory?.category_id || flowState?.category_id || 5
  );
  const selectedSubjectId = Number(
    flowState?.selectedSubject?.subject_id || flowState?.selectedSubject?.id || flowState?.subject_id || 50
  );

  useEffect(() => {
    persistRapidFireFlowState(flowState);
  }, [flowState]);

  const goToStartRapidFire = () => {
    navigate(RAPID_FIRE_ROUTES.start, {
      replace: true,
      state: flowState,
    });
  };

  const { data: questionsData, isLoading } = useRapidFireQuestions(
    {
      states_id: selectedStateId,
      category_id: selectedCategoryId,
      subject_id: selectedSubjectId,
      rapidfire_batch_id: batchId,
      page: 1,
    },
    { enabled: !!batchId }
  );

  const rawQuestions = questionsData?.question_list || questionsData?.questions || (Array.isArray(questionsData) ? questionsData : []);

  const questions = useMemo(() => {
    return rawQuestions.map((item, index) => {
      const questionIndex = Number(
        item?.question_index || item?.question_number || item?.serial_no || item?.sr_no || index + 1
      );
      return {
        id: item?.id || item?.question_id || item?.qb_question_id || index + 1,
        serialNo: questionIndex,
        question:
          item?.question ||
          item?.question_html ||
          item?.question_text ||
          item?.question_title ||
          item?.question_name ||
          item?.name ||
          item?.title ||
          "Question will be available soon.",
        questionSub: getLocalizedText(item, "question_sub") || "",
        options: Array.isArray(item?.question_option) ? item.question_option.map(normalizeOption) : [],
        raw: item,
      };
    });
  }, [rawQuestions]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [timer, setTimer] = useState(0);
  const [selectedOptionId, setSelectedOptionId] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [questionStates, setQuestionStates] = useState({});
  const [activeModal, setActiveModal] = useState(null);

  const currentQuestion = questions[currentIndex];
  const qState = questionStates[currentQuestion?.id] || {
    isBookmarked: Number(currentQuestion?.raw?.is_bookmarked || currentQuestion?.raw?.bookmark_status || 0) === 1,
    isLiked: Number(currentQuestion?.raw?.is_liked || currentQuestion?.raw?.like_status || 0) === 1,
    likeCount: Number(currentQuestion?.raw?.like_count || currentQuestion?.raw?.total_likes || 0),
    viewCount: Number(currentQuestion?.raw?.view_count || currentQuestion?.raw?.total_views || 0),
    reportCount: currentQuestion?.raw?.report_count || "0",
    notesCount: Number(currentQuestion?.raw?.notes_count || currentQuestion?.raw?.total_notes || 0),
  };

  const footerStatsData = [
    { key: "bookmark", icon: qState.isBookmarked ? "bi-bookmark-fill text-primary" : "bi-bookmark", label: "Bookmark" },
    { key: "like", icon: qState.isLiked ? "bi-heart-fill text-danger" : "bi-heart", label: "Like", value: qState.likeCount > 0 ? qState.likeCount : "" },
    { key: "view", icon: "bi-eye", label: "View", value: qState.viewCount > 0 ? qState.viewCount : "", modal: "view" },
    { key: "report", icon: "bi-exclamation-circle", label: "Report", value: qState.reportCount, modal: "report" },
    { key: "notes", icon: "bi-journal-text", label: "Notes", value: qState.notesCount > 0 ? qState.notesCount : "", modal: "notes" },
  ];

  useEffect(() => {
    window.history.pushState({ rapidFireDetailGuard: true }, "", window.location.href);

    const handlePopState = () => {
      goToStartRapidFire();
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [flowState]);

  useEffect(() => {
    let interval;
    if (currentQuestion && !isProcessing) {
      interval = setInterval(() => {
        setTimer((prev) => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [currentQuestion, isProcessing]);

  const goToAnswerSheet = () => {
    navigate(RAPID_FIRE_ROUTES.answerSheet, {
      replace: true,
      state: flowState,
    });
  };

  const handleOptionSelect = async (option) => {
    if (isProcessing || selectedOptionId) return;

    setSelectedOptionId(option.id);
    setIsProcessing(true);

    const isCorrect = option.isCorrect;

    try {
      await api.post("/rapidfire/question-answer-save", {
        question_id: currentQuestion?.id,
        user_answer_id: option.id,
        rapidfire_batch_id: batchId,
      }).catch(() => {});

      await api.post("/question-bank/store-time", {
        question_id: currentQuestion?.id,
        timer_seconds: timer,
        is_view: 1,
        batch_id: batchId,
      }).catch(() => {});
    } catch {
      // Keep the answer flow moving even if persistence fails.
    }

    setTimeout(() => {
      if (isCorrect) {
        if (currentIndex < questions.length - 1) {
          setCurrentIndex((prev) => prev + 1);
          setTimer(0);
          setSelectedOptionId(null);
          setIsProcessing(false);
        } else {
          goToAnswerSheet();
        }
      } else {
        goToAnswerSheet();
      }
    }, 1500);
  };

  const handleFooterClick = async (item) => {
    if (item.modal) {
      setActiveModal(item.modal);
      return;
    }

    if (!currentQuestion) return;

    if (item.key === "bookmark") {
      const nextStatus = qState.isBookmarked ? 0 : 1;
      setQuestionStates((prev) => ({
        ...prev,
        [currentQuestion.id]: { ...qState, isBookmarked: nextStatus === 1 },
      }));
      api.post("/bookmark", {
        question_id: currentQuestion.id,
        bookmark_status: nextStatus,
      }).catch(() => {});
    }

    if (item.key === "like") {
      const nextStatus = qState.isLiked ? 0 : 1;
      const nextCount = nextStatus === 1 ? qState.likeCount + 1 : Math.max(0, qState.likeCount - 1);
      setQuestionStates((prev) => ({
        ...prev,
        [currentQuestion.id]: { ...qState, isLiked: nextStatus === 1, likeCount: nextCount },
      }));
      api.post("/like", {
        question_id: currentQuestion.id,
        like_status: nextStatus,
      }).catch(() => {});
    }
  };

  const renderFooterNav = (variant) => (
    <div className={`footer-nav-fixed ${variant}`}>
      <div className="qbank-stats">
        {footerStatsData.map((stat) => (
          <button
            key={stat.key}
            type="button"
            className="qbank-stat"
            onClick={() => handleFooterClick(stat)}
          >
            <i className={`bi ${stat.icon}`}></i>

            <span>{stat.label}</span>

            {stat.value !== "" && stat.value !== 0 && <strong>{stat.value}</strong>}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <>
      <style>{`
        .homeDashboard-wrapper .main-content {
          height: 100vh;
          overflow: hidden;
          box-sizing: border-box;
          padding-bottom: 35px;
        }

        .homeDashboard-wrapper .page-content {
          height: 100%;
          overflow: hidden;
          padding-right: 0;
        }
      `}</style>
      <HomeDashboardLayout showHeader={false}>
        <div className="categories-page qbank-detail-page" style={{ background: "#fff" }}>
          <div className="categories-page-header">
            <div>
              <button
                type="button"
                className="categories-back-button"
                onClick={goToStartRapidFire}
              >
                <i className="bi bi-chevron-left"></i>
              </button>
            </div>
            <div>
              <h1 className="categories-page-title">Rapid Fire</h1>
            </div>
            <div>
              <HeaderProfileSection />
            </div>
          </div>

          <div className="qb-Detail-content">
            <div className="d-flex qbank-detail-main">
              <section className="qbank-LeftContent" aria-label="Question and answers">
                <div className="qbank-card qbank-card--question">
                  <div className="qbank-questionTop">
                    <span className="qbank-progress">{currentIndex + 1} of {questions.length || 1}</span>

                    <div className="qbank-timer" aria-label="Question timer">
                      <i className="bi bi-clock" aria-hidden="true" />
                      <span>{String(Math.floor(timer / 60)).padStart(2, "0")}:{String(timer % 60).padStart(2, "0")}</span>
                    </div>

                    <div className="qbank-toolbar" aria-label="Question tools">
                      {toolbarActions.map((action) => (
                        <button
                          key={action.label}
                          type="button"
                          className="qbank-toolbarBtn"
                          aria-label={action.label}
                        >
                          <i className={`bi ${action.icon}`} aria-hidden="true" />
                        </button>
                      ))}
                    </div>
                  </div>

                  {isLoading ? (
                    <div style={{ padding: "40px", textAlign: "center" }}>
                      Loading rapid fire questions...
                    </div>
                  ) : currentQuestion ? (
                    <div className="data-question-scroll">
                      <div className="qbank-questionBlock">
                        <h2 className="qbank-question" dangerouslySetInnerHTML={{ __html: currentQuestion.question }} />
                        {currentQuestion.questionSub && (
                          <p className="qbank-questionSub" dangerouslySetInnerHTML={{ __html: currentQuestion.questionSub }} />
                        )}
                      </div>

                      <div className="qbank-options" role="list" aria-label="Answer options">
                        {currentQuestion.options.map((option, idx) => {
                          let tone = "neutral";
                          if (selectedOptionId) {
                            if (option.isCorrect) tone = "correct";
                            else if (option.id === selectedOptionId && !option.isCorrect) tone = "wrong";
                          }

                          return (
                            <button
                              key={option.id}
                              type="button"
                              disabled={isProcessing}
                              onClick={() => handleOptionSelect(option)}
                              className={`qbank-option qbank-option--${tone}`}
                              aria-label={`Option ${option.label}`}
                            >
                              <span className="qbank-option__badge">{String.fromCharCode(65 + idx)}</span>
                              <span className="qbank-option__copy" dangerouslySetInnerHTML={{ __html: option.label }} />
                            </button>
                          );
                        })}
                      </div>

                      <label className="qbank-guess" htmlFor="guess-answer">
                        <span className="qbank-guess__check">
                          <input id="guess-answer" type="checkbox" />
                          <span className="qbank-guess__box" aria-hidden="true" />
                        </span>
                        <span className="qbank-guess__label">Guess Answer</span>
                        <div className="info-btn">
                          <i className="bi bi-info-circle-fill"></i>
                        </div>
                      </label>
                    </div>
                  ) : (
                    <div style={{ padding: "40px", textAlign: "center" }}>
                      No questions available for this rapid fire.
                    </div>
                  )}

                  {renderFooterNav("footer-nav-fixed--desktop")}
                </div>
              </section>
            </div>

            {renderFooterNav("footer-nav-fixed--mobile")}
          </div>
        </div>

        <ViewModal
          open={activeModal === "view"}
          onClose={() => setActiveModal(null)}
          question={currentQuestion}
        />

        <ReportModal
          open={activeModal === "report"}
          onClose={() => setActiveModal(null)}
          question={currentQuestion}
        />

        <StickyNotesPopup
          open={activeModal === "notes"}
          onClose={() => setActiveModal(null)}
          question={currentQuestion}
        />
      </HomeDashboardLayout>
    </>
  );
};

export default RapidFireDetail;
